import {
  IN_APP_REFERRAL_TIERS,
  InAppReferralLeadStatus,
  InAppReferralTier,
  type IInAppReferralStats,
} from '@commenda/types';

type ReferralBadgeVariant = 'gray' | 'error' | 'warning' | 'success';

interface TierRule {
  tier: InAppReferralTier;
  minApprovedReferrals: number;
  giftCardCapCents: number;
}

export class InAppReferralHelper {
  /** Production in every environment: a partner must see the published terms, not a staging copy. */
  static readonly TERMS_URL =
    'https://www.commenda.io/terms-and-conditions/referrals';

  static readonly TIER_RULES: TierRule[] = IN_APP_REFERRAL_TIERS;

  private static readonly TIER_NAMES: Record<InAppReferralTier, string> = {
    [InAppReferralTier.BRONZE]: 'Bronze',
    [InAppReferralTier.SILVER]: 'Silver',
    [InAppReferralTier.GOLD]: 'Gold',
    [InAppReferralTier.PLATINUM]: 'Platinum',
  };

  private static readonly STATUS_BADGES: Record<
    InAppReferralLeadStatus,
    { label: string; variant: ReferralBadgeVariant }
  > = {
    [InAppReferralLeadStatus.PENDING]: { label: 'Pending', variant: 'warning' },
    [InAppReferralLeadStatus.APPROVED]: {
      label: 'Approved',
      variant: 'success',
    },
    [InAppReferralLeadStatus.REJECTED]: { label: 'Rejected', variant: 'error' },
  };

  static getTierName(tier: InAppReferralTier): string {
    return this.TIER_NAMES[tier];
  }

  static getTierRule(tier: InAppReferralTier): TierRule {
    return (
      this.TIER_RULES.find((rule) => rule.tier === tier) ?? this.TIER_RULES[0]
    );
  }

  static getTierIndex(tier: InAppReferralTier): number {
    return this.TIER_RULES.findIndex((rule) => rule.tier === tier);
  }

  static getTrackFillFraction(stats: IInAppReferralStats): number {
    const segmentCount = this.TIER_RULES.length - 1;
    const currentIndex = this.getTierIndex(stats.tier);
    if (currentIndex >= segmentCount) return 1;

    const currentRule = this.TIER_RULES[currentIndex];
    const nextRule = this.TIER_RULES[currentIndex + 1];
    const span =
      nextRule.minApprovedReferrals - currentRule.minApprovedReferrals;
    const into = Math.min(
      Math.max(stats.approvedThisYear - currentRule.minApprovedReferrals, 0),
      span
    );
    return (currentIndex + (span > 0 ? into / span : 0)) / segmentCount;
  }

  static getTierHeadline(stats: IInAppReferralStats): string {
    if (!stats.nextTier || stats.approvedNeededForNextTier == null) {
      return 'You have reached Platinum, our top tier';
    }
    const noun =
      stats.approvedNeededForNextTier === 1 ? 'company' : 'companies';
    return `Refer ${stats.approvedNeededForNextTier} more ${noun} to reach ${this.getTierName(stats.nextTier)}`;
  }

  /** Quotes no lifetime total on purpose: caps apply at approval time, so any such figure lies. */
  static getTierSummary(stats: IInAppReferralStats): string {
    const tierName = this.getTierName(stats.tier);
    const cap = this.formatCents(stats.giftCardCapCents);
    const topRule = this.TIER_RULES[this.TIER_RULES.length - 1];
    const topCap = this.formatCents(topRule.giftCardCapCents);
    const yearNote =
      'Tiers are based on referrals approved this calendar year.';

    if (!stats.nextTier) {
      return `You are a ${tierName} partner. Each approved referral earns a gift card of up to ${cap}, matching what the business you referred actually buys. ${yearNote}`;
    }

    const nextRule = this.getTierRule(stats.nextTier);
    const nextCap = this.formatCents(nextRule.giftCardCapCents);
    return `You are a ${tierName} partner. Each approved referral earns a gift card of up to ${cap}, matching what the business you referred actually buys. That ceiling rises to ${nextCap} at ${this.getTierName(stats.nextTier)} and ${topCap} at ${this.getTierName(topRule.tier)}. ${yearNote}`;
  }

  static getStatusBadge(status: InAppReferralLeadStatus): {
    label: string;
    variant: ReferralBadgeVariant;
  } {
    return this.STATUS_BADGES[status];
  }

  static formatCents(cents: number): string {
    const dollars = cents / 100;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: dollars % 1 === 0 ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(dollars);
  }

  static formatSubmittedDate(iso: string): string {
    return new Date(iso).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }
}
